import { CoreEvent, eventNames } from '@carvana/core-events';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { useLoadableState } from '@carvana/vdp-react-hooks';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useUserData } from '~/contexts/UserContext';
import {
  FavoriteRequestBody,
  FavoriteVehicle,
  fetchFavoritesApi,
  addFavoriteApi,
  removeFavoriteApi
} from '~/services/favoritesService';
import { toError } from '~/utilities/errorHandlers';

const defaultContext = {
  addFavorite: _value => {},
  favorites: [],
  isLoading: false,
  modalVehicleId: 0,
  removeFavorite: _value => {},
  setModalVehicleId: _value => []
};

type FavoritesContextType = {
  addFavorite: (payload: FavoriteRequestBody) => void;
  favorites: FavoriteVehicle[];
  isLoading: boolean;
  modalVehicleId: number;
  removeFavorite: (payload: FavoriteRequestBody) => void;
  setModalVehicleId: (number) => void;
};

const FavoritesContext = createContext<FavoritesContextType>(defaultContext);

export function FavoritesContextProvider({ children }) {
  const { trackEvent } = useContext(AnalyticsContext);
  const { isLoggedIn } = useUserData();
  const {
    data: favorites,
    setData,
    isLoading,
    setLoading,
    setError
  } = useLoadableState<FavoriteVehicle[]>([]);
  const [modalVehicleId, setModalVehicleId] = useState(0);

  const fetchFavorites = useCallback(async () => {
    const data = await fetchFavoritesApi();
    setData(data?.hasHadFavorites ? data.vehicles : []);
  }, [setData]);

  const addFavorite = useCallback(
    async (payload: FavoriteRequestBody) => {
      setLoading();
      try {
        const newFavorite = await addFavoriteApi(payload);
        // A car can return a null response even if it was added because it’s rare but sometimes vdp or disocunts times out and then the vehicle returned may be null if the vdp/discount info wasn’t added. But it’s in the db. If it wasn’t added in the db it will return an error
        if (newFavorite && payload.vehicleId === newFavorite.vehicleId) {
          setData([newFavorite, ...favorites]);
        } else {
          await fetchFavorites();
        }
        trackEvent('favorite added', { vehicleId: payload.vehicleId });
      } catch (err) {
        setError(toError(err));
      }
    },
    [favorites, fetchFavorites, setData, setError, setLoading, trackEvent]
  );

  const removeFavorite = useCallback(
    async (payload: FavoriteRequestBody) => {
      await removeFavoriteApi(payload);
      trackEvent('favorite removed', { vehicleId: payload.vehicleId });
      setData(
        favorites.filter(favorite => favorite.vehicleId !== payload.vehicleId)
      );
    },
    [favorites, setData, trackEvent]
  );

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchFavorites();
  }, [fetchFavorites, isLoggedIn]);

  useEffect(() => {
    CoreEvent.on(eventNames.favorites.vehicle.removed, fetchFavorites);

    return () =>
      CoreEvent.off(eventNames.favorites.vehicle.removed, fetchFavorites);
  }, [fetchFavorites]);

  const value = useMemo<FavoritesContextType>(
    () => ({
      addFavorite,
      favorites,
      isLoading,
      modalVehicleId,
      removeFavorite,
      setModalVehicleId
    }),
    [addFavorite, favorites, isLoading, modalVehicleId, removeFavorite]
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

FavoritesContextProvider.displayName = 'FavoritesContextProvider';

export default FavoritesContext;
